import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";
import {
  fetchMarketMakerStatus,
  stopMarketMaker,
} from "../../services/apiServices";

const MarketMakerStatus = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stoppingMarkets, setStoppingMarkets] = useState(new Set());
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  // Load market maker status
  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await fetchMarketMakerStatus();
      
      if (response && response.success && response.data && response.data.sessions) {
        setSessions(response.data.sessions);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error("Error fetching market maker status:", error);
      setSessions([]);
      showAlert("Failed to load market maker status", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Show alert message
  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: "", type: "" });
    }, 5000);
  };

  // Handle stop market maker
  const handleStopMarketMaker = async (marketId) => {
    try {
      setStoppingMarkets(prev => new Set([...prev, marketId]));
      
      await stopMarketMaker(marketId);
      
      showAlert("Market maker stopped successfully", "success");
      
      // Reload the status to get updated data
      await loadStatus();
    } catch (error) {
      console.error("Error stopping market maker:", error);
      showAlert(
        error.response?.data?.message || 
        error.message || 
        "Failed to stop market maker", 
        "danger"
      );
    } finally {
      setStoppingMarkets(prev => {
        const newSet = new Set(prev);
        newSet.delete(marketId);
        return newSet;
      });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format progress percentage
  const formatProgress = (progress) => {
    return `${progress.toFixed(2)}%`;
  };

  useEffect(() => {
    loadStatus();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <h4 className="card-title mb-0">Market Maker Sessions</h4>
        </CardHeader>
        <CardBody className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading market maker sessions...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h4 className="card-title mb-0">Market Maker Sessions</h4>
        <Button color="secondary" size="sm" onClick={loadStatus}>
          Refresh
        </Button>
      </CardHeader>
      <CardBody>
        {alert.show && (
          <Alert color={alert.type} className="mb-3">
            {alert.message}
          </Alert>
        )}

        {sessions.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No active market maker sessions found.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="table-nowrap mb-0">
              <thead>
                <tr>
                  <th>Market ID</th>
                  <th>Session ID</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Total Trades</th>
                  <th>Total Volume</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.session_id}>
                    <td>
                      <code className="text-primary">
                        {session.market_id.substring(0, 8)}...
                      </code>
                    </td>
                    <td>
                      <code className="text-secondary">
                        {session.session_id.substring(0, 8)}...
                      </code>
                    </td>
                    <td>{formatDate(session.start_time)}</td>
                    <td>{formatDate(session.end_time)}</td>
                    <td>
                      <Badge color="info" pill>
                        {session.total_trades}
                      </Badge>
                    </td>
                    <td>
                      <Badge color="success" pill>
                        {session.total_volume.toFixed(2)}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="progress flex-grow-1 me-2" style={{ height: "6px" }}>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${Math.min(session.progress_percentage, 100)}%` }}
                          ></div>
                        </div>
                        <small className="text-muted">
                          {formatProgress(session.progress_percentage)}
                        </small>
                      </div>
                    </td>
                    <td>
                      <Badge 
                        color={session.is_active ? "success" : "secondary"}
                        pill
                      >
                        {session.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td>
                      {session.is_active && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleStopMarketMaker(session.market_id)}
                          disabled={stoppingMarkets.has(session.market_id)}
                        >
                          {stoppingMarkets.has(session.market_id) ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              Stopping...
                            </>
                          ) : (
                            "Stop"
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default MarketMakerStatus;